import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlaybookItemNote, { PlaybookNoteAffordance, PlaybookNoteEditor } from './PlaybookItemNote';
import { RootState, AppDispatch } from '../store';
import {
  fetchPlaybooks,
  fetchRoleData,
  selectPlaybookOptions,
  setSelectedPlaybook,
  setSelectedRole,
  toggleQuestionAsked,
  addQuestionNote,
  removeQuestionNote,
  togglePitchChecked,
  addPitchNote,
  removePitchNote,
  toggleAdvancedChecked,
  addAdvancedNote,
  removeAdvancedNote,
  resetLivePlaybookItem
} from '../store/slices/salesPlaybookSlice';
import { playbookChecklistWebSocketService } from '../services/playbookChecklistWebSocketService';
import { recallWebSocketService } from '../services/recallWebSocketService';
import { sessionManager } from '../services/sessionManager';
import { config } from '../config';
import {
  PLAYBOOK_NAME_LABEL,
  PLAYBOOK_SELECT_PLACEHOLDER,
  PLAYBOOK_EMPTY_STATE_TEXT,
  PLAYBOOK_VALUE_PLACEHOLDER,
  LEGACY_PLAYBOOK_NAME_LABEL,
  LEGACY_PLAYBOOK_SELECT_PLACEHOLDER,
  LEGACY_PLAYBOOK_EMPTY_STATE_TEXT,
} from '../constants/salesPlaybook';

interface SalesPlaybookPanelProps {
  height?: number;
  onHeightChange?: (height: number) => void;
  minHeight?: number;
  maxHeight?: number;
  isSessionActive?: boolean;
  /** When true, hides the playbook/role dropdowns (used in training where the playbook is pre-selected) */
  hideSelectors?: boolean;
  /** When true, the panel fills its parent's height (flex) and ignores the height prop */
  fillHeight?: boolean;
  /** Optional callback for playbook actions. When provided, called INSTEAD of recallWebSocketService for persistence. */
  onPlaybookAction?: (action: { updateType: string; [key: string]: any }) => void;
  /**
   * Set while the user watches someone else's call: the checklist stays read
   * only (pass `isSessionActive={false}`, so nothing is sent) and its disabled
   * items explain why with this text instead of "Start a session".
   */
  viewOnlyTitle?: string;
}

/** The two pickers: equal width, white, 1px border, radius 8, 12 text, about 26 tall, the chevron clear of the text. */
const PICKER_CLASS =
  'min-w-0 flex-1 rounded-control border border-ink-border bg-surface py-1 pl-2 pr-7 text-xs text-ink focus:ring-2 focus:ring-signature focus:border-signature';

/** A 14 checkbox: a muted outline, filled (signature, or success just after a live check) when checked. */
const CHECKBOX_CLASS = 'h-3.5 w-3.5 rounded-none border-ink-muted focus:ring-signature';

interface PlaybookSectionHeaderProps {
  title: string;
  count: number;
  isCollapsed: boolean;
  onToggle: () => void;
}

/** A section's full-width header on paper: chevron (right folded, down open), title, "(N)". The macOS section header. */
const PlaybookSectionHeader: React.FC<PlaybookSectionHeaderProps> = ({ title, count, isCollapsed, onToggle }) => (
  <button
    onClick={onToggle}
    className="flex w-full items-center gap-1.5 rounded-inner bg-paper px-2 py-1.5 text-left transition-colors hover:bg-surface-subtle"
  >
    <span className="flex w-3 flex-shrink-0 justify-center text-ink-secondary" aria-hidden="true">
      <svg
        className={`h-2.5 w-2.5 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M9 5l7 7-7 7" />
      </svg>
    </span>
    <span className="text-xs font-semibold text-ink">{title}</span>
    <span className="text-[11px] text-ink-muted">({count})</span>
  </button>
);

/** A live check's mark at the row's top right, while the row is green. */
const JustCompletedMark: React.FC = () => (
  <span role="img" aria-label="Just completed" className="absolute right-2 top-0.5 inline-flex text-success animate-check-flash">
    <CheckCircleIcon sx={{ fontSize: 14 }} />
  </span>
);

const SalesPlaybookPanel: React.FC<SalesPlaybookPanelProps> = ({
  height = 320,
  onHeightChange,
  minHeight = 150,
  maxHeight = 800,
  isSessionActive = false,
  hideSelectors = false,
  fillHeight = false,
  onPlaybookAction,
  viewOnlyTitle,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    playbooksLoading,
    selectedPlaybookName,
    selectedPlaybookDisplayName,
    selectedRole,
    selectedRoleDisplayName,
    availableRoles,
    currentPitchItems,
    currentAdvancedItems,
    questions,
    dataLoading,
    askedQuestions,
    questionNotes,
    checkedPitches,
    pitchNotes,
    checkedAdvanced,
    advancedNotes,
    liveCheckAnimations,
    liveNoteAnimations,
    error
  } = useSelector((state: RootState) => state.salesPlaybook);
  const playbookOptions = useSelector(selectPlaybookOptions);

  const [isPitchCollapsed, setIsPitchCollapsed] = useState(true);
  const [isAdvancedCollapsed, setIsAdvancedCollapsed] = useState(true);
  const [isQuestionsCollapsed, setIsQuestionsCollapsed] = useState(true);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [editingPitchNoteId, setEditingPitchNoteId] = useState<string | null>(null);
  const [pitchNoteText, setPitchNoteText] = useState('');
  const [editingAdvancedNoteId, setEditingAdvancedNoteId] = useState<string | null>(null);
  const [advancedNoteText, setAdvancedNoteText] = useState('');

  // Live-call surface label gating only: when the flag is OFF the previous
  // 'Industry' copy renders, but the data key read is always playbookName.
  const isPlaybookNameLabelEnabled = config?.features?.playbookNameLabelEnabled ?? true;
  const playbookFieldLabel = isPlaybookNameLabelEnabled ? PLAYBOOK_NAME_LABEL : LEGACY_PLAYBOOK_NAME_LABEL;
  const playbookSelectPlaceholder = isPlaybookNameLabelEnabled
    ? PLAYBOOK_SELECT_PLACEHOLDER
    : LEGACY_PLAYBOOK_SELECT_PLACEHOLDER;
  const playbookEmptyStateText = isPlaybookNameLabelEnabled
    ? PLAYBOOK_EMPTY_STATE_TEXT
    : LEGACY_PLAYBOOK_EMPTY_STATE_TEXT;

  // A live check or a live note opens its collapsed section, so the rep sees the
  // item turn green or the customer's note arrive.
  useEffect(() => {
    const sections = new Set([...Object.keys(liveCheckAnimations), ...Object.keys(liveNoteAnimations)]
      .map(key => key.split('|')[0]));
    if (sections.has('pitch')) setIsPitchCollapsed(false);
    if (sections.has('question')) setIsQuestionsCollapsed(false);
    if (sections.has('advanced')) setIsAdvancedCollapsed(false);
  }, [liveCheckAnimations, liveNoteAnimations]);

  // A newly shown playbook opens its first section with items and folds the
  // rest, so the rep can start without a click (owner, 2026-10-23). Once per
  // playbook and role: the rep's own toggles stand after that.
  const openedSelection = useRef<string | null>(null);
  useEffect(() => {
    const selection = `${selectedPlaybookName}|${selectedRole}`;
    if (!selectedPlaybookName || !selectedRole || dataLoading || openedSelection.current === selection) return;
    const first = currentPitchItems.length > 0 ? 'pitch'
      : questions.length > 0 ? 'question'
        : currentAdvancedItems.length > 0 ? 'advanced' : null;
    if (!first) return;
    openedSelection.current = selection;
    setIsPitchCollapsed(first !== 'pitch');
    setIsQuestionsCollapsed(first !== 'question');
    setIsAdvancedCollapsed(first !== 'advanced');
  }, [selectedPlaybookName, selectedRole, dataLoading, currentPitchItems.length, questions.length, currentAdvancedItems.length]);

  // Debug: Log when askedQuestions changes
  useEffect(() => {
    console.log('[SalesPlaybookPanel] askedQuestions updated:', askedQuestions, 'for role:', selectedRole);
  }, [askedQuestions, selectedRole]);

  // Fetch playbooks on mount
  useEffect(() => {
    dispatch(fetchPlaybooks());
  }, [dispatch]);

  // Fetch role data when playbook or role changes
  useEffect(() => {
    if (selectedPlaybookName && selectedRole) {
      console.log('[SalesPlaybookPanel] Fetching role data for:', selectedPlaybookName, selectedRole);
      dispatch(fetchRoleData({ playbookName: selectedPlaybookName, roleName: selectedRole }));
    }
  }, [dispatch, selectedPlaybookName, selectedRole]);

  // Route playbook actions to either the callback (training) or recallWebSocketService (live calls)
  const sendPlaybookAction = useCallback((action: { updateType: string; [key: string]: any }) => {
    if (onPlaybookAction) {
      onPlaybookAction(action);
      return;
    }
    // Live call path — use recallWebSocketService
    const roomId = sessionManager.getRoomId();
    if (!isSessionActive || !roomId || !recallWebSocketService.isConnected()) return;
    // Route selection/question updates to sendTalkTrackUpdate, others to sendSalesPlaybookUpdate
    if (['selection', 'question_asked', 'question_note'].includes(action.updateType)) {
      recallWebSocketService.sendTalkTrackUpdate({ roomId, ...action } as any);
    } else {
      recallWebSocketService.sendSalesPlaybookUpdate({ roomId, ...action } as any);
    }
  }, [onPlaybookAction, isSessionActive]);

  // Persist the selection whenever bot mode, playbook data, or the Recall
  // socket becomes ready. The socket can open after the first active render.
  useEffect(() => {
    if (onPlaybookAction) return; // Training mode — no auto-save via recallWebSocketService
    const syncSelection = () => {
      if (!isSessionActive || !selectedPlaybookName || !selectedRole) return;
      const roomId = sessionManager.getRoomId();
      if (roomId && recallWebSocketService.isConnected()) {
        recallWebSocketService.sendTalkTrackUpdate({
          roomId,
          updateType: 'selection',
          playbookName: selectedPlaybookName,
          playbookDisplayName: selectedPlaybookDisplayName || selectedPlaybookName,
          role: selectedRole,
          roleDisplayName: selectedRoleDisplayName || selectedRole,
          totalQuestionsAvailable: questions.length
        });
      }
    };
    const unsubscribe = recallWebSocketService.onConnected(syncSelection);
    syncSelection();
    return unsubscribe;
  }, [onPlaybookAction, isSessionActive, selectedPlaybookName,
      selectedPlaybookDisplayName, selectedRole, selectedRoleDisplayName, questions.length]);

  const handlePlaybookChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const playbookNameKey = e.target.value;
    if (!playbookNameKey) {
      dispatch(setSelectedPlaybook(null));
      return;
    }

    const playbook = playbookOptions.find(pb => pb?.playbookName === playbookNameKey);
    if (playbook) {
      dispatch(setSelectedPlaybook(playbook));

      sendPlaybookAction({
        updateType: 'selection',
        playbookName: playbookNameKey,
        playbookDisplayName: playbook.displayName,
        role: 'general',
        roleDisplayName: 'General',
        totalQuestionsAvailable: 0
      });
      playbookChecklistWebSocketService.refresh();
    }
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const roleName = e.target.value;
    const role = availableRoles.find(r => r.name === roleName);
    if (role) {
      dispatch(setSelectedRole(role));

      sendPlaybookAction({
        updateType: 'selection',
        playbookName: selectedPlaybookName,
        playbookDisplayName: selectedPlaybookDisplayName || '',
        role: role.name,
        roleDisplayName: role.displayName,
        totalQuestionsAvailable: questions.length
      });
      playbookChecklistWebSocketService.refresh();
    }
  };

  // Unchecking an item is its reset (owner, 2026-10-26): the check, its note and
  // the manual lock go, so the AI may check it again if the rep says it later.
  const handleResetItem = (section: 'pitch' | 'question' | 'advanced', id: string, text: string, legacyId?: string) => {
    dispatch(resetLivePlaybookItem({ section, id }));
    // Its note is gone, so an editor open on it closes too.
    if (section === 'question' && editingNoteId === id) {
      setEditingNoteId(null);
      setNoteText('');
    } else if (section === 'pitch' && editingPitchNoteId === id) {
      setEditingPitchNoteId(null);
      setPitchNoteText('');
    } else if (section === 'advanced' && editingAdvancedNoteId === id) {
      setEditingAdvancedNoteId(null);
      setAdvancedNoteText('');
    }
    sendPlaybookAction({
      updateType: 'reset_item', section,
      ...(section === 'question' ? { questionId: id, questionText: text }
        : section === 'pitch' ? { pitchId: id, pitchText: text, legacyId }
          : { advancedId: id, advancedText: text, legacyId }),
      playbookName: selectedPlaybookName || undefined,
      playbookDisplayName: selectedPlaybookDisplayName || undefined,
      role: selectedRole,
      roleDisplayName: selectedRoleDisplayName || undefined
    });
  };

  const handleQuestionToggle = (questionId: string) => {
    const question = questions.find(q => q._id === questionId);
    if (askedQuestions.includes(questionId)) {
      handleResetItem('question', questionId, question?.questionText || '');
      return;
    }

    dispatch(toggleQuestionAsked(questionId));

    // Auto-open note input when checking
    setEditingNoteId(questionId);
    setNoteText('');

    sendPlaybookAction({
      updateType: 'question_asked',
      questionId,
      questionText: question?.questionText || '',
      tags: question?.tags || [],
      isAsked: true,
      playbookName: selectedPlaybookName || undefined,
      playbookDisplayName: selectedPlaybookDisplayName || undefined,
      role: selectedRole || undefined,
      roleDisplayName: selectedRoleDisplayName || undefined
    });
  };

  const handleSaveNote = (questionId: string) => {
    const question = questions.find(q => q._id === questionId);
    const trimmedNote = noteText.trim();

    if (trimmedNote) {
      dispatch(addQuestionNote({ questionId, note: trimmedNote }));
    } else {
      dispatch(removeQuestionNote(questionId));
    }

    sendPlaybookAction({
      updateType: 'question_note',
      questionId,
      questionText: question?.questionText || '',
      note: trimmedNote,
      playbookName: selectedPlaybookName || undefined,
      playbookDisplayName: selectedPlaybookDisplayName || undefined,
      role: selectedRole || undefined,
      roleDisplayName: selectedRoleDisplayName || undefined
    });

    setEditingNoteId(null);
    setNoteText('');
  };

  // Pitch handlers
  const handlePitchToggle = (pitchId: string, pitchText: string, legacyId?: string) => {
    if (checkedPitches.includes(pitchId)) {
      handleResetItem('pitch', pitchId, pitchText, legacyId);
      return;
    }
    dispatch(togglePitchChecked(pitchId));

    // Auto-open note input when checking
    setEditingPitchNoteId(pitchId);
    setPitchNoteText('');

    sendPlaybookAction({
      updateType: 'pitch_checked',
      pitchId,
      pitchText,
      legacyId,
      isChecked: true,
      playbookName: selectedPlaybookName || undefined,
      playbookDisplayName: selectedPlaybookDisplayName || undefined,
      role: selectedRole || undefined,
      roleDisplayName: selectedRoleDisplayName || undefined
    });
  };

  const handleSavePitchNote = (pitchId: string, pitchText: string, legacyId?: string) => {
    const trimmedNote = pitchNoteText.trim();

    if (trimmedNote) {
      dispatch(addPitchNote({ pitchId, note: trimmedNote }));
    } else {
      dispatch(removePitchNote(pitchId));
    }

    sendPlaybookAction({
      updateType: 'pitch_note',
      pitchId,
      pitchText,
      legacyId,
      note: trimmedNote,
      playbookName: selectedPlaybookName || undefined,
      playbookDisplayName: selectedPlaybookDisplayName || undefined,
      role: selectedRole || undefined,
      roleDisplayName: selectedRoleDisplayName || undefined
    });

    setEditingPitchNoteId(null);
    setPitchNoteText('');
  };

  // Advanced handlers
  const handleAdvancedToggle = (advancedId: string, advancedText: string, legacyId?: string) => {
    if (checkedAdvanced.includes(advancedId)) {
      handleResetItem('advanced', advancedId, advancedText, legacyId);
      return;
    }
    dispatch(toggleAdvancedChecked(advancedId));

    // Auto-open note input when checking
    setEditingAdvancedNoteId(advancedId);
    setAdvancedNoteText('');

    sendPlaybookAction({
      updateType: 'advanced_checked',
      advancedId,
      advancedText,
      legacyId,
      isChecked: true,
      playbookName: selectedPlaybookName || undefined,
      playbookDisplayName: selectedPlaybookDisplayName || undefined,
      role: selectedRole || undefined,
      roleDisplayName: selectedRoleDisplayName || undefined
    });
  };

  const handleSaveAdvancedNote = (advancedId: string, advancedText: string, legacyId?: string) => {
    const trimmedNote = advancedNoteText.trim();

    if (trimmedNote) {
      dispatch(addAdvancedNote({ advancedId, note: trimmedNote }));
    } else {
      dispatch(removeAdvancedNote(advancedId));
    }

    sendPlaybookAction({
      updateType: 'advanced_note',
      advancedId,
      advancedText,
      legacyId,
      note: trimmedNote,
      playbookName: selectedPlaybookName || undefined,
      playbookDisplayName: selectedPlaybookDisplayName || undefined,
      role: selectedRole || undefined,
      roleDisplayName: selectedRoleDisplayName || undefined
    });

    setEditingAdvancedNoteId(null);
    setAdvancedNoteText('');
  };

  // Resize handling
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeight = height;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const newHeight = Math.min(maxHeight, Math.max(minHeight, startHeight + deltaY));
      onHeightChange?.(newHeight);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [height, minHeight, maxHeight, onHeightChange]);

  return (
    <div className={`flex flex-col overflow-hidden bg-surface rounded-card border border-hairline ${fillHeight ? 'min-h-0 flex-1' : ''}`} style={fillHeight ? undefined : { height }}>
      {/* Header: the macOS card's title row. No rules around the selectors (owner, 2026-10-26: too boxy). */}
      <div className="flex-shrink-0 px-2.5 py-1.5">
        <h3 className="text-sm font-semibold text-ink">Sales Playbook</h3>
      </div>
      {/* Selectors (or the playbook name in training), in a strip under the header; hidden when empty */}
      <div className="flex-shrink-0 px-2.5 py-1.5 empty:hidden">
        {hideSelectors && selectedPlaybookName && (
          <div className="text-xs text-ink-muted">
            {selectedPlaybookDisplayName || selectedPlaybookName || PLAYBOOK_VALUE_PLACEHOLDER}
            {selectedRoleDisplayName ? ` / ${selectedRoleDisplayName}` : selectedRole ? ` / ${selectedRole}` : ''}
          </div>
        )}
        {!hideSelectors && (
          <div className="flex gap-1.5">
            <select
              value={selectedPlaybookName || ''}
              onChange={handlePlaybookChange}
              disabled={playbooksLoading}
              aria-label={playbookFieldLabel}
              className={PICKER_CLASS}
            >
              <option value="">{playbookSelectPlaceholder}</option>
              {playbookOptions.map(playbook => (
                <option key={playbook?.playbookName || playbook?.displayName} value={playbook?.playbookName || ''}>
                  {playbook?.displayName}
                </option>
              ))}
            </select>
            <select
              value={selectedRole}
              onChange={handleRoleChange}
              disabled={!selectedPlaybookName || availableRoles.length === 0}
              className={PICKER_CLASS}
            >
              {availableRoles.map(role => (
                <option key={role.name} value={role.name}>
                  {role.displayName}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-1.5 min-h-0">
        {error && (
          <div className="text-error text-sm mb-2">{error}</div>
        )}

        {playbooksLoading || (hideSelectors && !selectedPlaybookName) ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2">
            <div className="w-6 h-6 border-2 border-signature border-t-transparent rounded-full animate-spin" />
            <span className="text-ink-muted text-sm">Loading playbook...</span>
          </div>
        ) : !selectedPlaybookName ? (
          <div className="text-ink-muted text-sm text-center py-4">
            {playbookEmptyStateText}
          </div>
        ) : dataLoading ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2">
            <div className="w-6 h-6 border-2 border-signature border-t-transparent rounded-full animate-spin" />
            <span className="text-ink-muted text-sm">Loading playbook data...</span>
          </div>
        ) : (
          <div className="space-y-1.5">
            {/* Intro Pitch Section */}
            {currentPitchItems.length > 0 && (
              <div>
                <PlaybookSectionHeader
                  title="Intro Pitch"
                  count={currentPitchItems.length}
                  isCollapsed={isPitchCollapsed}
                  onToggle={() => setIsPitchCollapsed(!isPitchCollapsed)}
                />
                {!isPitchCollapsed && (
                  <ul className="space-y-0.5 px-0.5 pt-0.5">
                    {currentPitchItems.map((bullet) => {
                      const pitchId = bullet.id;
                      const isChecked = checkedPitches.includes(pitchId);
                      const note = pitchNotes[pitchId];
                      const isEditingNote = editingPitchNoteId === pitchId;
                      const isFresh = !!liveCheckAnimations[`pitch|${pitchId}`];

                      return (
                        <li key={pitchId} className={`group relative rounded-inner py-0.5 transition-colors duration-700 ${isFresh ? 'bg-success-tint' : ''}`}>
                          <div className="flex items-start gap-2">
                            <span className="flex h-4 w-[18px] shrink-0 items-center justify-center">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handlePitchToggle(pitchId, bullet.text, bullet.legacyId)}
                                disabled={!isSessionActive}
                                className={`${CHECKBOX_CLASS} ${isFresh ? 'text-success' : 'text-signature'} ${!isSessionActive ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={!isSessionActive ? viewOnlyTitle ?? 'Start a session to mark pitches' : ''}
                              />
                            </span>
                            <div className={`flex min-w-0 flex-1 items-start text-xs ${isFresh ? 'pr-6' : ''}`}>
                              <span className={`transition-colors duration-700 ${isFresh ? 'text-success font-medium' : isChecked ? 'line-through text-ink-muted' : 'text-ink'}`}>
                                {bullet.text}
                              </span>
                              <PlaybookNoteAffordance
                                isChecked={isChecked}
                                note={note?.note}
                                isHidden={isEditingNote || !!liveNoteAnimations[`pitch|${pitchId}`]}
                                onEdit={isSessionActive ? () => {
                                  setEditingPitchNoteId(pitchId);
                                  setPitchNoteText(note?.note ?? '');
                                } : undefined}
                              />
                            </div>
                          </div>
                          {isFresh && <JustCompletedMark />}
                          <div className="pl-[26px]">
                            {/* Note display/edit */}
                            {isSessionActive && isEditingNote ? (
                              <PlaybookNoteEditor
                                value={pitchNoteText}
                                savedNote={note?.note}
                                onChange={setPitchNoteText}
                                onSave={() => handleSavePitchNote(pitchId, bullet.text, bullet.legacyId)}
                                onCancel={() => { setEditingPitchNoteId(null); setPitchNoteText(''); }}
                              />
                            ) : note && liveNoteAnimations[`pitch|${pitchId}`] ? (
                              <PlaybookItemNote note={note.note} />
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {/* Qualifying Questions - flat ordered list */}
            {questions.length > 0 && (
              <div>
                <PlaybookSectionHeader
                  title="Qualifying Questions"
                  count={questions.length}
                  isCollapsed={isQuestionsCollapsed}
                  onToggle={() => setIsQuestionsCollapsed(!isQuestionsCollapsed)}
                />
                {!isQuestionsCollapsed && (
                  <ul className="space-y-0.5 px-0.5 pt-0.5">
                    {questions.map(q => {
                      const isAsked = askedQuestions.includes(q._id);
                      const note = questionNotes[q._id];
                      const isEditingNote = editingNoteId === q._id;
                      const isFresh = !!liveCheckAnimations[`question|${q._id}`];

                      return (
                        <li key={q._id} className={`group relative rounded-inner py-0.5 transition-colors duration-700 ${isFresh ? 'bg-success-tint' : ''}`}>
                          <div className="flex items-start gap-2">
                            <span className="flex h-4 w-[18px] shrink-0 items-center justify-center">
                              <input
                                type="checkbox"
                                checked={isAsked}
                                onChange={() => handleQuestionToggle(q._id)}
                                disabled={!isSessionActive}
                                className={`${CHECKBOX_CLASS} ${isFresh ? 'text-success' : 'text-signature'} ${!isSessionActive ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={!isSessionActive ? viewOnlyTitle ?? 'Start a session to mark questions' : ''}
                              />
                            </span>
                            <div className={`flex min-w-0 flex-1 items-start text-xs ${isFresh ? 'pr-6' : ''}`}>
                              <span className={`transition-colors duration-700 ${isFresh ? 'text-success font-medium' : isAsked ? 'line-through text-ink-muted' : 'text-ink'}`}>
                                {q.questionText}
                              </span>
                              <PlaybookNoteAffordance
                                isChecked={isAsked}
                                note={note?.note}
                                isHidden={isEditingNote || !!liveNoteAnimations[`question|${q._id}`]}
                                onEdit={isSessionActive ? () => {
                                  setEditingNoteId(q._id);
                                  setNoteText(note?.note ?? '');
                                } : undefined}
                              />
                            </div>
                          </div>
                          {isFresh && <JustCompletedMark />}
                          <div className="pl-[26px]">
                            {isSessionActive && isEditingNote ? (
                              <PlaybookNoteEditor
                                value={noteText}
                                savedNote={note?.note}
                                onChange={setNoteText}
                                onSave={() => handleSaveNote(q._id)}
                                onCancel={() => { setEditingNoteId(null); setNoteText(''); }}
                              />
                            ) : note && liveNoteAnimations[`question|${q._id}`] ? (
                              <PlaybookItemNote note={note.note} />
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {/* Advanced Section */}
            {currentAdvancedItems.length > 0 && (
              <div>
                <PlaybookSectionHeader
                  title="Deal Progression"
                  count={currentAdvancedItems.length}
                  isCollapsed={isAdvancedCollapsed}
                  onToggle={() => setIsAdvancedCollapsed(!isAdvancedCollapsed)}
                />
                {!isAdvancedCollapsed && (
                  <ul className="space-y-0.5 px-0.5 pt-0.5">
                    {currentAdvancedItems.map((bullet) => {
                      const advancedId = bullet.id;
                      const isChecked = checkedAdvanced.includes(advancedId);
                      const note = advancedNotes[advancedId];
                      const isEditingNote = editingAdvancedNoteId === advancedId;
                      const isFresh = !!liveCheckAnimations[`advanced|${advancedId}`];

                      return (
                        <li key={advancedId} className={`group relative rounded-inner py-0.5 transition-colors duration-700 ${isFresh ? 'bg-success-tint' : ''}`}>
                          <div className="flex items-start gap-2">
                            <span className="flex h-4 w-[18px] shrink-0 items-center justify-center">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleAdvancedToggle(advancedId, bullet.text, bullet.legacyId)}
                                disabled={!isSessionActive}
                                className={`${CHECKBOX_CLASS} ${isFresh ? 'text-success' : 'text-signature'} ${!isSessionActive ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={!isSessionActive ? viewOnlyTitle ?? 'Start a session to mark items' : ''}
                              />
                            </span>
                            <div className={`flex min-w-0 flex-1 items-start text-xs ${isFresh ? 'pr-6' : ''}`}>
                              <span className={`transition-colors duration-700 ${isFresh ? 'text-success font-medium' : isChecked ? 'line-through text-ink-muted' : 'text-ink'}`}>
                                {bullet.text}
                              </span>
                              <PlaybookNoteAffordance
                                isChecked={isChecked}
                                note={note?.note}
                                isHidden={isEditingNote || !!liveNoteAnimations[`advanced|${advancedId}`]}
                                onEdit={isSessionActive ? () => {
                                  setEditingAdvancedNoteId(advancedId);
                                  setAdvancedNoteText(note?.note ?? '');
                                } : undefined}
                              />
                            </div>
                          </div>
                          {isFresh && <JustCompletedMark />}
                          <div className="pl-[26px]">
                            {/* Note display/edit */}
                            {isSessionActive && isEditingNote ? (
                              <PlaybookNoteEditor
                                value={advancedNoteText}
                                savedNote={note?.note}
                                onChange={setAdvancedNoteText}
                                onSave={() => handleSaveAdvancedNote(advancedId, bullet.text, bullet.legacyId)}
                                onCancel={() => { setEditingAdvancedNoteId(null); setAdvancedNoteText(''); }}
                              />
                            ) : note && liveNoteAnimations[`advanced|${advancedId}`] ? (
                              <PlaybookItemNote note={note.note} />
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {currentPitchItems.length === 0 && questions.length === 0 && !dataLoading && (
              <div className="text-ink-muted text-sm text-center py-4">
                No content for this role yet
              </div>
            )}
          </div>
        )}
      </div>

      {/* Resize handle */}
      {onHeightChange && (
        <div
          className="h-2 bg-surface-subtle cursor-ns-resize flex items-center justify-center hover:bg-surface-sunken border-t border-hairline"
          onMouseDown={handleResizeStart}
        >
          <div className="w-8 h-1 bg-ink-muted rounded" />
        </div>
      )}
    </div>
  );
};

// Backward compatibility alias
export const TalkTracksPanel = SalesPlaybookPanel;

export default SalesPlaybookPanel;
